import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  useUnsavedChangesNavigation,
  type BlockedNavigation,
} from "contexts/unsaved-changes-navigation-context";

const isModifiedClick = (event: MouseEvent) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const getInternalNavigationPath = (href: string) => {
  if (href.startsWith("/")) {
    return href;
  }

  try {
    const url = new URL(href, window.location.origin);

    if (url.origin !== window.location.origin) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

type UseUnsavedChangesNavigationGuardOptions = {
  when: boolean;
  onNavigateAttempt: (navigation: BlockedNavigation) => void;
};

export const useUnsavedChangesNavigationGuard = ({
  when,
  onNavigateAttempt,
}: UseUnsavedChangesNavigationGuardOptions) => {
  const navigate = useNavigate();
  const { setActiveGuard, requestNavigation } = useUnsavedChangesNavigation();
  const onNavigateAttemptRef = useRef(onNavigateAttempt);

  useEffect(() => {
    onNavigateAttemptRef.current = onNavigateAttempt;
  }, [onNavigateAttempt]);

  useEffect(() => {
    if (!when) {
      setActiveGuard(null);
      return;
    }

    setActiveGuard({
      onNavigateAttempt: (navigation) => onNavigateAttemptRef.current(navigation),
    });

    return () => {
      setActiveGuard(null);
    };
  }, [setActiveGuard, when]);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (isModifiedClick(event)) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a");

      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }

      const href = anchor.getAttribute("href");

      if (!href || href.startsWith("#")) {
        return;
      }

      const nextPath = getInternalNavigationPath(href);

      if (!nextPath) {
        return;
      }

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      if (nextPath === currentPath) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      requestNavigation({ run: () => navigate(nextPath) });
    };

    document.addEventListener("click", handleClick, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
    };
  }, [navigate, requestNavigation, when]);
};
